import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import UploadedFileCard from '../FileUpload/UploadedFileCard';
import DraggableUploadCard from '../FileUpload/DraggableUploadCard';
import YoutubeIcon from '../../assets/YoutubeIcon.svg';

import { GoChevronDown } from 'react-icons/go';

import uuid from 'short-uuid';

import { useSocket } from '../../Context/SocketContext';
import { FaRegSave } from 'react-icons/fa';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { scrollbarStyles } from '../Constants';

const Body = () => {
  const ytLinkRef = useRef(null);
  const textNotesRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [fileURLs, setFileURLs] = useState([]); // To store the URLs of the uploaded files
  const socket = useSocket();

  const history = useHistory();
  const location = useLocation();

  const [newOption, setNewOption] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('Course Name');
  const [isUploading, setIsUploading] = useState(false);

  const [courseNames, setCourseNames] = useState([
    'Hindi',
    'English',
    'Maths',
    'Science',
    'Social Studies',
  ]);

  const { setTopLevelError, handleConfirmationModal } = useAuth();

  // emits associateLink event to the server
  const handleGenerateNotes = () => {
    const links = ytLinkRef?.current?.value?.split('\n');

    const studyChatError =
      'Please upload some study materials or paste some youtube links to generate notes';
    const testMeModeError =
      'Please upload some study materials or paste some youtube links to generate quiz';

    const payload = {
      ytLinks: links?.filter(link => link !== ''),
      fileURLs,
      course: selectedCourse,
    };

    if (payload?.ytLinks?.length === 0 && payload?.fileURLs?.length === 0) {
      if (location.pathname === '/testMeMode') {
        setTopLevelError(testMeModeError);
        return;
      }

      setTopLevelError(studyChatError);
      return;
    }

    if (socket) {
      socket.emit('associateLink', payload);
    }
  };

  // creates a new option for the dropdown
  const handleCreateNewOption = () => {
    if (newOption === '' || courseNames.includes(newOption)) return;
    // Handle logic for creating a new option here

    setCourseNames(prev => [...prev, newOption]);
    setSelectedCourse(newOption);
    setNewOption('');
  };

  // Add text notes, creates a new dummy file and then calls handleFileUpload
  const handleAddTextNotes = e => {
    e.preventDefault();

    const text = textNotesRef.current.value;
    let title = e.currentTarget.title.value;

    if (!text || !socket) return;

    if (!title) title = 'Untitled';
    const randomID = uuid.generate();

    // Convert text content to a Blob
    const newFile = new File([text], title + '.txt', { type: 'text/plain' });
    newFile.id = randomID;
    newFile.progress = 0;

    setFiles(prev => [...prev, newFile]);

    handleFileUpload(newFile);
  };

  // Delete a file from the list
  const handleFileDelete = id => {
    // Delete the file from DB

    setFiles(prev => prev.filter(file => file.id !== id));
  };

  // Handle file actions like delete, retry, cancel
  const handleFileAction = (id, action) => {
    // Perform the action here

    if (action === 'delete') {
      setFiles(prev => prev.filter(file => file.id !== id));
    } else if (action === 'retry') {
      const file = files.find(file => file.id === id);
      setFiles(prev =>
        prev.map(file => {
          if (file.id === id) {
            file.progress = 0;
            file.error = undefined;
          }
          return file;
        })
      );
      handleFileUpload(file);
    } else if (action === 'cancel') {
      // Cancel the upload

      setFiles(prev =>
        prev.map(file => {
          if (file.id === id) {
            file.progress = 0;
            file.error = true;
          }
          return file;
        })
      );
    }
  };

  // Upload a file in chunks
  const handleFileUpload = uploadedFile => {
    const chunkSize = 5 * 1024 * 1024; // 5MB
    const fileSize = uploadedFile.size;
    const fileName = uploadedFile.name;
    const fileType = uploadedFile.type;
    const id = uploadedFile.id;

    let offset = 0;
    let chunkIndex = 0;

    const reader = new FileReader();

    reader.onload = () => {
      const chunk = reader.result;
      socket.emit('uploadChunk', {
        data: chunk,
        chunkIndex,
        fileName,
        fileSize,
        offset,
        isFinalChunk: offset + chunk.byteLength >= fileSize,
        fileType,
        id,
        // courseName: selectedCourse,
      });

      offset += chunk.byteLength;
      chunkIndex++;
      // const newProgress = Math.floor((offset / fileSize) * 100);
      // setProgress(newProgress);

      if (offset < fileSize) {
        readNextChunk();
      }
    };

    const readNextChunk = () => {
      const slice = uploadedFile.slice(offset, offset + chunkSize);
      reader.readAsArrayBuffer(slice); // Use readAsArrayBuffer to read binary data
    };

    readNextChunk();
  };

  // Handle the files uploaded from the dropdown
  const handleDropDownChange = uploadedFiles => {
    uploadedFiles.map(file => (file.id = uuid.generate()));
    // uploadedFiles.forEach((file, index) => handleFileUpload(file));

    setFiles(prev => [...prev, ...uploadedFiles]);
    uploadedFiles.forEach(file => handleFileUpload(file));

    return;
  };

  // Listen for chunk upload status and associationID events
  // when associationID is received, navigate to the smart notes page, or go to configure quiz page
  useEffect(() => {
    if (!socket) return;

    socket.on('chunkUploadStatus', data => {
      const { success, fileName, id, progress, isFinalChunk, downloadURL } =
        data;

      setFiles(prev => {
        const updatedFiles = prev.map(file => {
          if (file.id === id && !file.error) {
            if (success) {
              file.progress = progress;
              if (isFinalChunk) {
                file.downloadURL = downloadURL;

                setFileURLs(prev => [
                  ...prev,
                  {
                    name: fileName,
                    url: downloadURL,
                    type: file.type,
                    id: id,
                  },
                ]);

                // setFileURLs(prev => [...prev, downloadURL]);
                file.error = false;
              }
            } else {
              file.progress = 0;
              file.error = true;
            }
          }
          return file;
        });

        return updatedFiles;
      });
    });

    socket.on('associationID', data => {
      const { success, associationID } = data;
      const parsedCourseName =
        selectedCourse === 'Course Name' ? 'Unnamed' : selectedCourse;
      if (success) {
        if (location.pathname === '/testMeMode') {
          history.push(
            `/configureQuiz?notesId=${associationID}&course=${parsedCourseName}`
          );
        } else {
          // navigate to smart notes page
          history.push(
            `/smartNotes?id=${associationID}&course=${parsedCourseName}`
          );
        }
      } else {
        console.error('Error generating notes');
        setTopLevelError('Error generating notes. Please try again later!');
      }
    });

    return () => {
      socket.off('chunkUploadStatus');
      socket.off('associationID');
    };
  }, [socket, history, selectedCourse, setTopLevelError]);

  useEffect(() => {
    // check if any file has file?.error === undefined

    const inprogressUpload = files.find(file => file?.error === undefined);
    setIsUploading(!!inprogressUpload);
  }, [files]);

  return (
    <>
      <Flex justifyContent="center" color="#1B2559">
        <Menu>
          <MenuButton bg="white" as={Button} rightIcon={<GoChevronDown />}>
            {selectedCourse}
          </MenuButton>
          <MenuList overflowY="auto" maxHeight="20rem" sx={scrollbarStyles}>
            <MenuOptionGroup
              value={selectedCourse}
              onChange={val => setSelectedCourse(val)}
            >
              {courseNames.map((courseName, idx) => (
                <MenuItemOption key={idx} value={courseName}>
                  {courseName}
                </MenuItemOption>
              ))}
            </MenuOptionGroup>
            <Flex justifyContent="center">
              <InputGroup width="90%">
                <Input
                  placeholder="Enter new option"
                  value={newOption}
                  onChange={e => setNewOption(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleCreateNewOption();
                  }}
                  // onBlur={handleCreateNewOption}
                />
                <InputRightElement width="3.5rem">
                  <IconButton
                    h="1.75rem"
                    p="0.4rem"
                    icon={<FaRegSave />}
                    // as={FaRegSave}
                    size="md"
                    onClick={handleCreateNewOption}
                    cursor="pointer"
                  />
                </InputRightElement>
              </InputGroup>
            </Flex>
          </MenuList>
        </Menu>
      </Flex>

      <SimpleGrid
        minChildWidth="250px"
        maxBlockSize="38rem"
        pr="5px"
        spacing="40px"
        mt="0.6rem"
      >
        {/* <Box> */}
        <Flex
          direction="column"
          justifyContent="center"
          alignItems="center"
          color="#1B2559"
          height="100%"
        >
          <Box
            maxW="400px"
            width="100%"
            border="1px solid #5B35FF"
            padding="12px"
            borderRadius="10px"
            height="100%"
          >
            <Text textAlign="center" fontSize="1.2rem">
              Uploaded Study Materials
            </Text>
            <Divider mb="5px" />
            <Box
              maxH="30rem"
              overflowY="auto"
              sx={{
                '&::-webkit-scrollbar': {
                  width: '10px', // Width of the scrollbar
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888', // Color of the scrollbar handle
                  borderRadius: '5px', // Rounded corners
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1', // Color of the scrollbar track
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#555', // Darker color when hovered
                },
                paddingRight: '10px',
              }}
            >
              <VStack
                direction="column"
                justifyContent="center"
                spacing={2}
                height="100%"
              >
                {files.length === 0 && (
                  <Text fontWeight="400" mt="0.9rem">
                    No study material uploaded yet
                  </Text>
                )}
                {files.map(file => (
                  <UploadedFileCard
                    key={file.id}
                    fileObj={file}
                    progress={file.progress ?? 0}
                    handleAction={handleFileAction}
                    handleDelete={() => handleFileDelete(file.id)}
                  />
                ))}
              </VStack>
            </Box>
          </Box>
        </Flex>
        {/* </Box> */}
        <Flex
          direction="column"
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <Flex direction="column" maxW="35rem" height="100%" width="100%">
            <Box>
              <DraggableUploadCard
                isStudyChat={true}
                multiple={true}
                handleChange={handleDropDownChange}
              />
            </Box>
            <Flex
              direction="column"
              alignItems="center"
              pt="5px"
              border="1px dashed #4318FF"
              borderRadius="10px"
              padding="10px"
              mt="5px"
            >
              <Image
                src={YoutubeIcon}
                w={{ base: '3rem', md: '6rem' }}
                h={{ base: '3rem', md: '6rem' }}
              />
              <Input
                ref={ytLinkRef}
                name="youtubeLink"
                placeholder="Paste Youtube Link Here.."
              />
              {/* <Textarea
                ref={ytLinkRef}
                placeholder="Paste Youtube Link Here.."
                resize={'none'}
                sx={scrollbarStyles}
              /> */}
            </Flex>
          </Flex>
        </Flex>
        <Flex
          direction="column"
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <Flex direction="column" maxW="35rem" height="100%" width="100%">
            <Flex
              direction="column"
              justifyContent="center"
              height="100%"
              border="1px solid #5B35FF"
              //   padding="12px"
              borderRadius="10px"
              p="0.7rem"
            >
              <Textarea
                placeholder="Type or Copy & Paste your study materials here ..."
                height="100%"
                ref={textNotesRef}
                resize={'none'}
                minH="20rem"
                sx={scrollbarStyles}
              />
              <Flex pt="0.4rem">
                <form
                  style={{ display: 'flex', width: '100%' }}
                  onSubmit={handleAddTextNotes}
                >
                  <Input name="title" placeholder="Title your text" />
                  <Button
                    bg="linear-gradient(15.46deg, #4A25E1 26.3%, #7B5AFF 86.4%)"
                    _hover={{
                      background:
                        'linear-gradient(15.46deg, #183f73 26.3%, #7B5AFF 86.4%)',
                    }}
                    color="white"
                    ml="0.3rem"
                    type="submit"
                  >
                    Add
                  </Button>
                </form>
              </Flex>
            </Flex>

            <Flex justifyContent="center">
              <Button
                disabled={isUploading}
                opacity={isUploading ? 0.5 : 1}
                cursor={isUploading ? 'not-allowed' : 'pointer'}
                loadingText="Uploading files..."
                mt="10px"
                background="linear-gradient(89.2deg, #3D1DFF 9.32%, #6147FF 28.54%, #D451FF 60.81%, #D451FF 63.38%, #EC458D 83.98%)"
                color="#fff"
                _hover={{
                  background:
                    'linear-gradient(15.46deg, #4A25E1 26.3%, #7B5AFF 86.4%)',
                }}
                // onClick={handleGenerateNotes}
                onClick={e => {
                  if (location.pathname === '/testMeMode') {
                    handleGenerateNotes(e);
                    return;
                  }
                  handleConfirmationModal(() => handleGenerateNotes(e), 3);
                }}
              >
                {location.pathname === '/testMeMode'
                  ? 'Configure Quiz'
                  : 'Generate SMART Notes'}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </SimpleGrid>
    </>
  );
};

export default Body;
